import {
  Box,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import { AppIconButton } from '../design-system';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import type { GatewayConfigLayer, OverrideLayerStatus } from './gateway-config-layer-state';

export interface GatewayConfigLayerRow {
  layer: GatewayConfigLayer;
  label: string;
  scope: string;
  persistenceLabel: string;
  persistenceIcon?: 'lock' | 'edit' | 'push';
  persistenceColor?: 'primary' | 'warning';
  status: OverrideLayerStatus | { label: string; color: OverrideLayerStatus['color'] };
  statusLoading?: boolean;
  action: 'view' | 'edit';
  actionDisabled?: boolean;
  actionAriaLabel: string;
}

export interface GatewayConfigLayersTableProps {
  title?: string;
  description?: string;
  rows: GatewayConfigLayerRow[];
  onOpenLayer: (layer: GatewayConfigLayer) => void;
}

const tableSx = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '4px',
  overflow: 'hidden',
  '& td, & th': { py: 1.25, px: 2, borderBottom: '1px solid', borderColor: 'divider' },
  '& th': { fontWeight: 600, textAlign: 'left', color: 'text.secondary', bgcolor: 'action.hover' },
} as const;

function persistenceIcon(icon: GatewayConfigLayerRow['persistenceIcon']) {
  if (icon === 'lock') return <LockIcon style={{ fontSize: 13 }} />;
  if (icon === 'push') return <PlayArrowIcon style={{ fontSize: 13 }} />;
  return <EditIcon style={{ fontSize: 13 }} />;
}

export function GatewayConfigLayersTable({
  title = 'Config Layers Summary',
  description,
  rows,
  onOpenLayer,
}: GatewayConfigLayersTableProps) {
  return (
    <Box sx={{ mb: description ? 2 : 4 }}>
      <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="text.secondary">
        {title}
      </Typography>
      {description ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
      ) : null}
      <Box component="table" sx={tableSx}>
        <thead>
          <tr>
            <th>Layer</th>
            <th>Scope</th>
            <th>Persistence</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.layer}>
              <td>{row.label}</td>
              <td>{row.scope}</td>
              <td>
                <Chip
                  size="small"
                  label={row.persistenceLabel}
                  icon={persistenceIcon(row.persistenceIcon ?? (row.action === 'view' ? 'lock' : 'edit'))}
                  color={row.persistenceColor ?? (row.action === 'view' ? 'default' : 'primary')}
                  variant="outlined"
                  sx={{ height: 22, fontSize: 11 }}
                />
              </td>
              <td>
                {row.statusLoading ? (
                  <CircularProgress size={14} />
                ) : (
                  <Chip
                    size="small"
                    label={row.status.label}
                    color={row.status.color}
                    sx={{ height: 22, fontSize: 11 }}
                  />
                )}
              </td>
              <td>
                <Tooltip title={row.action === 'view' ? 'View' : 'Edit'}>
                  <span>
                    <AppIconButton
                      size="small"
                      onClick={() => onOpenLayer(row.layer)}
                      disabled={row.actionDisabled}
                      aria-label={row.actionAriaLabel}
                    >
                      {row.action === 'view' ? (
                        <VisibilityIcon fontSize="small" />
                      ) : (
                        <EditIcon fontSize="small" />
                      )}
                    </AppIconButton>
                  </span>
                </Tooltip>
              </td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}
